/* Version: 3.5.149 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { forwardRef } from 'react';
import { ExternalLink } from '@manulife/mux-cds-icons';
import PropTypes from 'prop-types';
import i18n from '../../lang/i18n/index.js';
import { safeRest, rem, noop } from '../../utils/functions.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { LINK_VARIANT } from './constants.js';
import { TextLinkComponent } from './styledComponents.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useTheme from '../../hooks/useTheme.js';
import useLang from '../../hooks/useLang.js';

var _excluded = ["id", "as", "to", "customStyle", "children", "isExternal", "isDocument", "icon", "variant", "onClick"];
var TextLink = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var controlledId = _ref.id,
    _ref$as = _ref.as,
    as = _ref$as === undefined ? 'a' : _ref$as,
    to = _ref.to,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      linkStyle: {}
    } : _ref$customStyle,
    children = _ref.children,
    _ref$isExternal = _ref.isExternal,
    isExternal = _ref$isExternal === undefined ? false : _ref$isExternal,
    _ref$isDocument = _ref.isDocument,
    isDocument = _ref$isDocument === undefined ? false : _ref$isDocument,
    icon = _ref.icon,
    _ref$variant = _ref.variant,
    variant = _ref$variant === undefined ? LINK_VARIANT.DEFAULT : _ref$variant,
    _ref$onClick = _ref.onClick,
    onClick = _ref$onClick === undefined ? noop : _ref$onClick,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useDerivedState = useDerivedState(useId('textlink-id-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var safeProps = safeRest(rest);
  var _safeCustomStyle = safeCustomStyle(customStyle),
    linkStyle = _safeCustomStyle.linkStyle;
  var theme = useTheme();
  var lang = useLang();
  var linkProps;
  if (as === 'a') {
    linkProps = {
      href: to,
      target: isExternal || isDocument ? '_blank' : '_self'
    };
  } else if (as === 'button') {
    linkProps = {
      type: 'button',
      onClick: onClick
    };
  } else {
    linkProps = {
      to: to,
      target: '_self'
    };
  }
  return /*#__PURE__*/jsxs(TextLinkComponent, _objectSpread(_objectSpread(_objectSpread({
    id: id,
    as: as,
    onClick: onClick,
    ref: ref,
    "aria-label": isExternal ? "".concat(children, " ").concat(i18n[lang].textLink.externalLinkText) : children,
    target: isExternal || isDocument ? '_blank' : '_self',
    rel: isExternal ? 'noopener noreferrer' : undefined,
    variant: variant,
    "data-hasicon": !!icon,
    theme: theme,
    linkStyle: linkStyle
  }, linkProps), safeProps), {}, {
    children: [icon && /*#__PURE__*/jsx("span", {
      className: "mux-TextLink-icon",
      children: icon
    }), children, isExternal && /*#__PURE__*/jsx("span", {
      className: "mux-TextLink-external-icon",
      children: /*#__PURE__*/jsx(ExternalLink, {
        title: "external-icon",
        fontSize: rem(14)
      })
    })]
  }));
});
process.env.NODE_ENV !== "production" ? TextLink.propTypes = {
  /** ID is required for Automated Testing, Google analytics and Google Tag Manager */
  id: PropTypes.string,
  /** component to use as TextLink ('a', 'button', or Link from react-router-dom) */
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** onClick handler */
  onClick: PropTypes.func,
  /** url (by default maps to href) or url/object/function (when using Link from react-router-dom) */
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.func]),
  /** Flag to create external link that opens in new tab */
  isExternal: PropTypes.bool,
  /** Flag to indicate a document link that opens in a new tab */
  isDocument: PropTypes.bool,
  /**
   * Icon displayed on the left.
   *
   * Refer to our documentation on how to pass one of the following icons to this prop:
   *
   * [CDS icons](/docs/icons) /
   * [Manulife icons](/docs/icons#manulife-icons) /
   * [Custom icons](/docs/icons#custom-icons).
   * */
  icon: PropTypes.node,
  /** Type of Text link */
  variant: PropTypes.oneOf([LINK_VARIANT.DEFAULT, LINK_VARIANT.INVERSE]),
  /** Content for the link */
  children: PropTypes.node.isRequired,
  /** Style overrides */
  customStyle: PropTypes.shape({
    linkStyle: PropTypes.object
  })
} : undefined;

export { TextLink as default };
